<template>
  <transition name="sf-fade">
    <div
      v-if="isVisible"
      class="notification-banner
        flex justify-between
        border border-l-4 py-2.5 px-4"
      :class="[borderColor, backgroundColor]"
    >
      <div
        class="notification-banner__first-half flex"
      >
        <span
          v-if="shouldShowIcon"
          class="mt-1 mr-8 h-4 flex items-center"
          :class="iconClass"
        />
        <div
          class="notification-banner__explanation"
          :class="fontSizeClass"
        >
          <span
            v-if="title"
            class="explanation__title font-bold mr-3"
          >{{ title }}</span>
          <slot />
          <span
            v-if="subtitle"
            class="explanation__subtitle"
          >{{ subtitle }}</span>
          <span v-if="errorArray.length">
            <ul>
              <li
                v-for="item in errorArray"
                :key="item"
              >
                {{ item }}
              </li>
            </ul>
          </span>
        </div>
      </div>
      <div
        class="notification-banner__second-half
          flex gap-4 items-start"
      >
        <div
          v-if="endingLinkText"
        >
          <SimpleLink
            class="underline text-EXPONDO-primary-60"
            :to="endingLinkSlug"
          >
            {{ endingLinkText }}
          </SimpleLink>
        </div>
        <div
          v-if="endingButtonText"
        >
          <CustomButton
            type="primary"
            size="sm"
            class="w-32 min-w-max"
            :text="endingButtonText"
            @click="$emit('click')"
          />
        </div>
        <button
          v-if="dismissible"
          class="rotate-45 transform-gpu"
          @click="closeNotification"
        >
          <span class="iconify svg--accordion-open text-xs text-EXPONDO-grey" />
        </button>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">

import type { NotificationStatus } from '~~/types/components/Notification/NotificationStatus';
import { NOTIFICATION_STATUS } from '~~/types/components/Notification/NotificationStatus';
import { ButtonTextSize } from '~~/types/components/Buttons/ButtonTextSize';
import { notificationCssClasses } from '~~/helpers/notifications/notificationCssClasses';

export default defineComponent({
  name: 'NotificationBanner',

  props: {
    status: {
      type: String as PropType<NotificationStatus>,
      default: NOTIFICATION_STATUS.info,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
    endingLinkText: {
      type: String,
      default: '',
    },
    endingLinkSlug: {
      type: String,
      default: '',
    },
    endingButtonText: {
      type: String,
      default: '',
    },
    dismissible: {
      type: Boolean,
      default: true,
    },
    shouldShow: {
      type: Boolean,
      default: false,
    },
    shouldShowIcon: {
      type: Boolean,
      default: true,
    },
    duration: {
      type: Number,
      default: 5000,
    },
    fontSizeClass: {
      type: String,
      default: 'text-base',
    },
    errorArray: {
      type: Array as PropType<string[] | string[]>,
      default: () => [],
    },
  },
  emits: ['click', 'close'],
  setup(props, { emit }) {

    const isActive = ref(true);
    const closeNotification = () => {

      isActive.value = false; 
      emit('close');

    };
    const isVisible = computed(() => isActive.value && (props.shouldShow || props.title || props.subtitle ||
      props.errorArray.length || props.endingLinkText || props.endingButtonText));

    const borderColor = computed(() => notificationCssClasses[props.status]?.border || '');
    const backgroundColor = computed(() => notificationCssClasses[props.status]?.background || '');
    const iconClass = computed(() => notificationCssClasses[props.status]?.icon || '');

    return {
      borderColor,
      backgroundColor,
      iconClass,
      ButtonTextSize,
      isVisible,
      closeNotification,
    };
  
  },
});

</script>
